import { useMutation, UseMutationResult, useQuery, UseQueryOptions, UseQueryResult } from 'react-query'

import { apiClients, makeClientQueryFn } from '@percept/hooks'

import qs from 'query-string'

import { saveAs } from 'file-saver'

import { filterTruthyMapping, isoDateUTC } from '@percept/utils'

import { AxiosError } from 'axios'

import { PerformanceProvider, ReportProviderV2, TimeseriesGranularity } from '@percept/types'


export type DataWarehouseReportGroupingBase = {
  title: string
  key: string
  index: number
  info: string | null
  supported_providers: DataWarehouseProvider[]
}

export type DataWarehouseReportGrouping = (
  DataWarehouseReportGroupingBase & {
    sub_groupings?: DataWarehouseReportGroupingBase[]
  }
)

export type DataWarehouseProvider = (
  ReportProviderV2 | PerformanceProvider
)

export type BasePerformanceSegmentation = 'NONE' | 'ORG_UNIT' | 'PROVIDER'

export type DataWarehouseQueryResponseRow = {
  dimensions: Record<string, string>
  metrics: Record<string, number | null>
  metadata: {
    min_freshness: number
    max_freshness: number
    missing_currency: number
    unique_currencies: string[]
  }
}

export type DataWarehouseQueryResponse = {
  metadata: {
    period: {
      start_date: string
      end_date: string
    }
    currency: string
    freshness: {
      min: number
      max: number
    }
    dimensions: Record<string, {
      label: string
      info: string | null
      internal?: boolean
    }>
    providers: DataWarehouseProvider[]
  }
  rows: DataWarehouseQueryResponseRow[]
}


export const useDataWarehouseQueryGroupings = (): UseQueryResult<DataWarehouseReportGrouping[], AxiosError<{ detail: string }>> => {
  return useQuery({
    queryKey: ['dataWarehouse', 'groupings'],
    queryFn: makeClientQueryFn<DataWarehouseReportGrouping[]>(
      apiClients.core,
      { url: '/performance/available-groupings' }
    ),
    cacheTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}


export type DataWarehouseQueryHookProps = {
  grouping: string | null
  sub_grouping?: string | null
  org_unit_ids: string[] | null
  providers?: string[] | null
  start: Date | null
  end: Date | null
  timeseries_granularity?: TimeseriesGranularity | null
  currency: string
  aggregate_to_single_org_unit?: boolean
  use_rollups?: boolean
  base_segmentations?: BasePerformanceSegmentation[] | null
} & Partial<UseQueryOptions<DataWarehouseQueryResponse, AxiosError<{ detail: string }>>>

const getDateParams = (
  { start, end }: Pick<DataWarehouseQueryHookProps, 'start' | 'end'>
): Record<'start_date' | 'end_date', string | null> => {
  return {
    start_date: start && isoDateUTC(start),
    end_date: end && isoDateUTC(end)
  }
}

export const useDataWarehouseQuery = ({
  grouping,
  sub_grouping,
  org_unit_ids,
  providers,
  start,
  end,
  timeseries_granularity,
  currency,
  aggregate_to_single_org_unit,
  enabled = true,
  use_rollups = true,
  base_segmentations = ['ORG_UNIT', 'PROVIDER'],
  ...options
}: DataWarehouseQueryHookProps): UseQueryResult<DataWarehouseQueryResponse, AxiosError<{ detail: string }>> => {
  const { start_date, end_date } = getDateParams({ start, end })
  return useQuery({
    queryKey: [
      'dataWarehouse',
      grouping,
      sub_grouping,
      providers && providers.sort(),
      start_date,
      end_date,
      timeseries_granularity,
      org_unit_ids && org_unit_ids.sort(),
      aggregate_to_single_org_unit,
      base_segmentations,
      currency,
      use_rollups,
    ],
    queryFn: makeClientQueryFn<DataWarehouseQueryResponse>(
      apiClients.core,
      {
        url: '/performance/query',
        params: filterTruthyMapping({
          org_unit_ids,
          providers,
          start_date,
          end_date,
          grouping,
          sub_grouping,
          timeseries_granularity,
          target_currency: currency,
          aggregate_to_single_org_unit,
          base_segmentations,
          use_rollups,
        }),
        paramsSerializer: qs.stringify
      },
    ),
    enabled: !!(enabled && grouping && org_unit_ids && org_unit_ids.length && start && end && providers && providers.length),
    cacheTime: 1000 * 60 * 1,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  })
}


export const useDataWarehouseExport = (): UseMutationResult<void, unknown, DataWarehouseQueryHookProps> => {
  return useMutation({
    mutationFn: async ({
      grouping,
      sub_grouping,
      org_unit_ids,
      providers,
      start,
      end,
      timeseries_granularity,
      currency,
      aggregate_to_single_org_unit,
      base_segmentations = ['ORG_UNIT', 'PROVIDER'],
    }: DataWarehouseQueryHookProps) => {
      const { start_date, end_date } = getDateParams({ start, end })
      const response = await apiClients.core.get<Blob>(
        '/performance/query/export',
        {
          params: {
            org_unit_ids,
            providers,
            start_date,
            end_date,
            timeseries_granularity,
            grouping,
            sub_grouping,
            target_currency: currency,
            aggregate_to_single_org_unit,
            base_segmentations,
          },
          paramsSerializer: qs.stringify,
          responseType: 'blob',
          headers: {
            'Content-Type': 'text/csv',
          }
        }
      )

      // Get filename from content disposition header if available
      const contentDisposition: string = response.headers && response.headers['content-disposition']
      let filename = 'report_export.csv'
      if( grouping ){
        filename = `${grouping.toLowerCase()}_report_export.csv`
      }
      if( contentDisposition ){
        const headerValues = contentDisposition.split(';').map( s => s.trim())
        const filenameValue = headerValues.find( v => v.startsWith('filename='))
        if( filenameValue ){
          filename = filenameValue.replace('filename=', '')
        }
      }

      saveAs(response.data, filename)
    }
  })
}


export type DataWarehouseAvailabilityResponse = {
  org_unit_id: string
  provider: DataWarehouseProvider
  grouping: string
  start: string
  end: string
  has_active_accounts: boolean
}

export const useDataWarehouseAvailability = ({
  org_unit_id
}: {
  org_unit_id: string | null
}): UseQueryResult<DataWarehouseAvailabilityResponse[], AxiosError<{ detail: string }>> => {
  return useQuery({
    queryKey: ['dataWarehouse', 'availability', org_unit_id],
    queryFn: makeClientQueryFn<DataWarehouseAvailabilityResponse[]>(
      apiClients.core,
      {
        url: `/performance/data-availability/org-units/${org_unit_id}`,
        params: {},
        paramsSerializer: qs.stringify
      },
    ),
    enabled: !!org_unit_id,
    cacheTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
